import {
  Puzzle,
  Eye,
  MessagesSquare,
  Waves,
  CalendarCheck,
  HeartHandshake,
  Apple,
  GraduationCap,
  LifeBuoy,
  Sprout,
  Users,
  Scale,
  ListChecks,
  HelpCircle,
  ClipboardList,
} from "lucide-react";

export const chapters = [
  {
    n: "01",
    title: "O que é o autismo",
    icon: Puzzle,
    tint: "bg-brandlight text-brand",
    span: "md:col-span-7",
    desc: "O TEA como condição do neurodesenvolvimento: um espectro de características, necessidades e potencialidades.",
    bullets: [
      "O TEA é uma condição do neurodesenvolvimento — não uma doença a ser “curada”.",
      "Cada pessoa autista é única: comunicação, interesses e necessidades de apoio variam.",
      "O cuidado foca na pessoa: comunicação, segurança, autonomia e qualidade de vida.",
    ],
  },
  {
    n: "02",
    title: "Como pode se manifestar",
    icon: Eye,
    tint: "bg-amber-100 text-amber-700",
    span: "md:col-span-5",
    desc: "Sinais possíveis no dia a dia — e por que nenhuma lista substitui uma avaliação.",
    bullets: [
      "Dificuldade com pistas sociais, preferência por rotinas e interesses intensos.",
      "Movimentos repetitivos e respostas diferentes a sons, luzes e texturas.",
    ],
    note: "Nem toda característica precisa estar presente. Evite autodiagnóstico por listas.",
  },
  {
    n: "03",
    title: "Comunicação e interação",
    icon: MessagesSquare,
    tint: "bg-cyan-100 text-cyan-700",
    span: "md:col-span-4",
    desc: "Formas respeitosas de se comunicar — com ou sem fala.",
    bullets: [
      "Comunicação clara, concreta e com tempo para processar.",
      "Ausência de fala não significa ausência de compreensão.",
      "A comunicação pode ser verbal, gestual, escrita, visual ou alternativa.",
    ],
    note: "Contato visual não é sinônimo de atenção.",
  },
  {
    n: "04",
    title: "Sensibilidade sensorial",
    icon: Waves,
    tint: "bg-brandlight text-brand",
    span: "md:col-span-4",
    desc: "Reconhecer sinais de sobrecarga e adaptar o ambiente.",
    bullets: [
      "Sons, luzes, cheiros e texturas podem sobrecarregar.",
      "Sinais: fuga, irritação, silêncio repentino, aumento de estereotipias.",
      "Reduza estímulos e ofereça espaços calmos.",
    ],
    note: "Não interprete respostas sensoriais como birra.",
  },
  {
    n: "05",
    title: "Rotina e previsibilidade",
    icon: CalendarCheck,
    tint: "bg-orange-100 text-orange-700",
    span: "md:col-span-4",
    desc: "Estratégias para mudanças e transições com menos ansiedade.",
    bullets: [
      "Rotinas previsíveis reduzem a ansiedade e facilitam a participação.",
      "Use calendários, listas, imagens e avisos prévios.",
      "Nas mudanças, explique o que será diferente e o que permanece igual.",
    ],
  },
  {
    n: "06",
    title: "Cuidados no dia a dia",
    icon: HeartHandshake,
    tint: "bg-rose-100 text-rose-700",
    span: "md:col-span-8",
    desc: "Sono, higiene, saúde e habilidades práticas — com respeito ao ritmo de cada pessoa.",
    bullets: [
      "Observe sono, alimentação, higiene, atividade física e sinais de dor ou doença.",
      "Divida tarefas complexas em passos simples e ensine uma habilidade por vez.",
      "Respeite privacidade, consentimento e limites do corpo.",
      "Use interesses especiais como ponte para aprendizagem e participação.",
    ],
  },
  {
    n: "07",
    title: "Alimentação, sono e saúde",
    icon: Apple,
    tint: "bg-green-100 text-green-700",
    span: "md:col-span-4",
    desc: "Quando observar, quando investigar e quando procurar apoio.",
    bullets: [
      "Seletividade alimentar pode envolver textura, cheiro, temperatura e rotina.",
      "Alterações persistentes de sono merecem avaliação cuidadosa.",
      "Nem tudo é “do autismo”: dor e mudanças de comportamento pedem investigação médica.",
    ],
    note: "Medicamentos e suplementos somente com orientação profissional.",
  },
  {
    n: "08",
    title: "Escola e aprendizagem",
    icon: GraduationCap,
    tint: "bg-brandlight text-brand",
    span: "md:col-span-6",
    desc: "O que uma boa inclusão escolar envolve na prática.",
    bullets: [
      "Expectativas adequadas, acessibilidade e parceria entre escola e família.",
      "Recursos úteis: instruções visuais, tempo extra, pausas e redução de estímulos.",
      "Metas que incluam comunicação, autonomia, participação e bem-estar.",
    ],
  },
  {
    n: "09",
    title: "Crises e desregulação",
    icon: LifeBuoy,
    tint: "bg-red-100 text-red-700",
    span: "md:col-span-6",
    desc: "Como agir durante a crise — e o que entender depois dela.",
    bullets: [
      "Priorize a segurança: fale pouco, com voz calma, e reduza estímulos.",
      "Não discuta, não ameace e não exija explicações durante a crise.",
      "Depois da recuperação, identifique gatilhos, sinais iniciais e necessidades.",
    ],
    note: "Em situação de risco: afaste objetos perigosos e busque ajuda adequada.",
  },
  {
    n: "10",
    title: "Desenvolvimento de autonomia",
    icon: Sprout,
    tint: "bg-green-100 text-green-700",
    span: "md:col-span-4",
    desc: "Autonomia não é fazer tudo sozinho.",
    bullets: [
      "Autonomia é participar das decisões e ter controle sobre a própria vida.",
      "Ensine rotinas de autocuidado e organização gradualmente.",
      "Ofereça escolhas reais: roupa, comida, ordem das atividades.",
    ],
  },
  {
    n: "11",
    title: "Família e rede de apoio",
    icon: Users,
    tint: "bg-orange-100 text-orange-700",
    span: "md:col-span-8",
    desc: "Cuidar de quem cuida: organização, divisão de tarefas e apoio confiável.",
    bullets: [
      "Cuidar também exige organização e suporte para o cuidador.",
      "Divida tarefas, registre informações e mantenha diálogo entre família, escola e profissionais.",
      "Desenvolvimento não é competição: evite comparações.",
      "Busque redes e profissionais que respeitem a dignidade e a comunicação da pessoa.",
    ],
  },
  {
    n: "12",
    title: "Direitos e inclusão",
    icon: Scale,
    tint: "bg-amber-100 text-amber-700",
    span: "md:col-span-5",
    desc: "A Lei nº 12.764/2012 e os direitos da pessoa com TEA.",
    bullets: [
      "A lei institui a Política Nacional de Proteção dos Direitos da Pessoa com TEA.",
      "Direitos incluem saúde, educação, acessibilidade e participação na comunidade.",
      "Confirme informações em fontes oficiais para cada situação.",
    ],
  },
  {
    n: "13",
    title: "Checklist de cuidados",
    icon: ListChecks,
    tint: "bg-cyan-100 text-cyan-700",
    span: "md:col-span-7",
    desc: "Um checklist prático para revisar o cuidado no cotidiano.",
    bullets: [
      "“Tenho uma forma de comunicação que funciona para esta pessoa.”",
      "“Conheço os principais gatilhos e sinais de sobrecarga.”",
      "“Incentivo a autonomia sem retirar o apoio necessário.”",
    ],
  },
  {
    n: "14",
    title: "Perguntas frequentes",
    icon: HelpCircle,
    tint: "bg-brandlight text-brand",
    span: "md:col-span-6",
    desc: "Respostas diretas para as dúvidas mais comuns sobre o TEA.",
    bullets: [
      "Autismo tem cura? Não — o foco é desenvolvimento e qualidade de vida.",
      "Toda pessoa autista tem deficiência intelectual? Não, são condições diferentes.",
      "Crise é sempre comportamento voluntário? Não — pode ser desregulação por sobrecarga.",
    ],
  },
  {
    n: "15",
    title: "Plano de apoio individual",
    icon: ClipboardList,
    tint: "bg-rose-100 text-rose-700",
    span: "md:col-span-6",
    desc: "Um modelo para preencher e organizar o cuidado de cada pessoa.",
    bullets: [
      "Registre comunicação, apoios necessários, gatilhos e estratégias que ajudam.",
      "Inclua contatos profissionais importantes e um plano de emergência.",
      "Uma ferramenta viva para revisar e atualizar com o tempo.",
    ],
  },
];
